import { useQuery } from "@tanstack/solid-query";
import type { Accessor } from "solid-js";
import { axiosInstance } from "@/api/client";
import { REPORT_APPLICANT_CAP } from "../constants";
import { type ReportFilters, toFiltersPayload } from "../types";

/**
 * Conteggio reale dei candidati di un'offerta.
 *
 * Esiste perché `job_offers.applications_count` non è affidabile: non viene mai
 * decrementata, la incrementa anche un click su un apply esterno tramite un
 * endpoint pubblico, e i dati seminati la riempiono a caso. Il backend che
 * genera il report conta invece le righe vere, quindi la UI che decide *se* e
 * *come* generarlo deve contare dalla stessa fonte, o mostra un cap e una
 * stima riferiti a una platea che non esiste.
 *
 * Modulo separato da `offer-report.ts` e privo di `solid-sonner`: quell'import
 * tocca `window` e romperebbe i test in node-env.
 */

export const REPORT_AUDIENCE_KEY = "report-candidate-count";

export type CandidateCount = {
  count: number;
  cap: number;
  capped: boolean;
};

export const fetchCandidateCount = async (
  jobOfferId: string,
  filters: ReportFilters,
): Promise<CandidateCount> => {
  const { data } = await axiosInstance.post(
    `/v1/ats/job-offers/${jobOfferId}/report/candidate-count`,
    { filters: toFiltersPayload(filters) },
  );
  return data as CandidateCount;
};

export const fetchCandidateCounts = async (
  jobOfferIds: string[],
): Promise<Record<string, number>> => {
  const { data } = await axiosInstance.post(
    "/v1/ats/job-offers/report/candidate-counts",
    { job_offer_ids: jobOfferIds },
  );
  return (data as { counts: Record<string, number> }).counts;
};

/** Conteggio filtrato di una singola offerta, per il dialog. */
export const useReportCandidateCount = (
  jobOfferId: Accessor<string | null | undefined>,
  filters: Accessor<ReportFilters>,
  enabled: Accessor<boolean> = () => true,
) =>
  useQuery(() => ({
    // I filtri entrano nella chiave in forma canonica, così due selezioni
    // equivalenti riusano la stessa risposta invece di interrogare due volte.
    queryKey: [
      REPORT_AUDIENCE_KEY,
      jobOfferId(),
      JSON.stringify(toFiltersPayload(filters()) ?? {}),
    ],
    queryFn: () => fetchCandidateCount(jobOfferId() as string, filters()),
    enabled: Boolean(jobOfferId()) && enabled(),
    staleTime: 30_000,
  }));

/**
 * Conteggi non filtrati per una pagina di card.
 *
 * Una richiesta per pagina, non una per card: il bottone download vive dentro
 * ogni riga della lista, e un hook per riga moltiplicherebbe le chiamate.
 */
export const useReportCandidateCounts = (
  jobOfferIds: Accessor<string[]>,
  enabled: Accessor<boolean> = () => true,
) =>
  useQuery(() => ({
    queryKey: [
      REPORT_AUDIENCE_KEY,
      "batch",
      [...jobOfferIds()].sort().join(","),
    ],
    queryFn: () => fetchCandidateCounts(jobOfferIds()),
    enabled: jobOfferIds().length > 0 && enabled(),
    staleTime: 30_000,
  }));

export { REPORT_APPLICANT_CAP };
